/* eslint-disable no-unused-vars */
import pinia from '@/plugins/pinia';
import {CapacitorHttp, HttpResponse} from '@capacitor/core';
import {watch} from 'vue';
import {ApiError} from './meta/error';
import {ApiResponse, ApiErrorResponse, ApiSuccessResponse, ApiErrorResponseAlt} from './types';
import {ResourceLager} from './module/warehouse';
import {ResourceSettings} from './module/settings';
import {ArtikelAction, ResourceArtikel} from './module/article';
import {PicklistAction, ResourcePicklist} from './module/picklist';
import {Query, serializeQuery} from './meta/odata';
import {CustomerAction, ResourceKunde} from './module/provider';
import {EingangslieferscheinAction, ResourceEingangslieferschein} from './module/incoming';
import {ResourceNone} from './module/misc';
import {OrderAction, ResourceBestellung} from './module/order';
import {ResourceInventur} from './module/inventory';
import {ResourceAnsprechpartner} from './module/contact';
import {ResourceAuftrag} from './module/task';
import {ResourceVerkaufslieferschein} from './module/deliveryNote';
import {ResourceRechnung} from './module/bill';
import {ResourceDms} from './module/dms';
import {base64ToBlob} from '@/util';
import {ContactHistoryAction, ResourceKontakt} from './module/contactHistory';
import {ResourceKontaktArt} from './module/contactKind';
import {ResourceDisponent} from './module/dispatcher';
import {ResourceNationality} from './module/nationality';
import {ResourceAngebot} from './module/offer';
import {BedienerAction} from './module/followup';
import {ResourceBediener} from './module/bediener';
import {useSettings} from '@/store/settings';
import {ResourceUmbuchung, UmbuchungAction} from '@/api/module/umbuchung';
import {BestandskorrekturAction, ResourceBestandskorrektur} from '@/api/module/correction';
import {ResourceNVE} from '@/api/module/nve';

const apiBase = 'https://api.service-erp.de';

export enum Accept {
  Json = 'application/json',
  OctetStream = 'application/octet-stream',
}

export enum Method {
  Get = 'GET',
  Post = 'POST',
  Delete = 'DELETE',
  Patch = 'PATCH',
  Put = 'PUT',
}

export enum Module {
  Artikel = '/service/artikel',
  Lager = '/service/lager',
  Picklist = '/service/pickliste',
  Bestellung = '/service/bestellung',
  Inventur = '/service/inventur',
  Eingangslieferschein = '/service/eingangslieferschein',
  Verkaufslieferschein = '/service/verkaufslieferschein',
  Kunde = '/service/kunde',
  Stammdaten = '/service/stammdaten',
  Dms = '/service/dms',
  Auftrag = '/service/auftrag',
  Rechnung = '/service/rechnungsausgang',
  Settings = '/service/settings',
  NVE = '/service/nve',
  None = '',
}

enum Api {
  V1 = '/api/v1',
}

type Action = PicklistAction
| EingangslieferscheinAction
| OrderAction
| CustomerAction
| BedienerAction
| ContactHistoryAction
| ArtikelAction
| UmbuchungAction
| BestandskorrekturAction;


type Resource = ResourcePicklist
  | ResourceAnsprechpartner
  | ResourceArtikel
  | ResourceAuftrag
  | ResourceBediener
  | ResourceBestellung
  | ResourceDisponent
  | ResourceDms
  | ResourceEingangslieferschein
  | ResourceInventur
  | ResourceKontakt
  | ResourceKontaktArt
  | ResourceKunde
  | ResourceLager
  | ResourceNone
  | ResourceNationality
  | ResourceAngebot
  | ResourceRechnung
  | ResourceSettings
  | ResourceNVE
| ResourceVerkaufslieferschein
  | ResourceUmbuchung
  | ResourceBestandskorrektur;


function isError<T>(response: ApiResponse<T>): response is ApiErrorResponse | ApiErrorResponseAlt {
  return !!((response as ApiErrorResponse).error) || !!((response as ApiErrorResponseAlt).Error);
}

function sanitizeError(sanitizedError: ApiErrorResponse | ApiErrorResponseAlt): ApiErrorResponse {
  if ((sanitizedError as ApiErrorResponse).error) return sanitizedError as ApiErrorResponse;

  const {
    Error: error,
    Type: type,
    RequestId: requestId,
    Code: code,
    ...oldError
  } = sanitizedError as ApiErrorResponseAlt;

  return {
    error,
    type,
    requestId,
    code,
    ...oldError,
  };
}

type ApiResponseSingle<T extends Exclude<any, any[]>> = [T];

type ApiResponseMulti<T extends any[]> = [T, number];

export type CombinedApiResponse<T> = T extends any[] ? ApiResponseMulti<T> : ApiResponseSingle<T>;

interface ApiCacheItem {
  updatedTimestamp: number;
  data: Promise<HttpResponse>;
}

let apiCache: Partial<{
  [customerId: string]: Partial<{
    [clientId: number]: Partial<{
      [url: string]: ApiCacheItem
    }>
  }>
}> = {};

export function init() {
  // reset cache on auth
  watch(() => useSettings(pinia).customerId, () => {
    apiCache = {};
  });
}

function insertActionParameters(action?: string, actionParameters?: Record<string, string | number>): string {
  if (!action) return '';
  if (!actionParameters) return action;

  return Object.entries(actionParameters).reduce((cum, [k, v]) => {
    return cum.replaceAll(`{${k}}`, v.toString());
  }, action);
}

interface GeneralApiRequestOptions extends Query {
  module: Module;
  resource: Resource;
  action?: Action;
  actionParameters?: Record<string, string | number>;
  accept?: Accept;
  id?: number | string;
  method: Method;
  data?: any;
  useCache?: boolean;
  cacheMaxSeconds?: number;
  client?: number;
  customerId?: string;
  allowBeforeAuth?: boolean;
  timeout?: number;
  bearerToken?: string;
  one_shot?: boolean;
  queryFields?: Record<string, string>;
}

export interface ApiJsonRequestOptions extends GeneralApiRequestOptions {
  accept?: Accept.Json;
}

export interface ApiBlobRequestOptions extends GeneralApiRequestOptions {
  accept: Accept.OctetStream;
}

export type ApiRequestOptions = ApiJsonRequestOptions | ApiBlobRequestOptions;

async function doActualApiRequest<T = any>({
  module,
  resource,
  action,
  actionParameters,
  accept = Accept.Json,
  method,
  filters,
  sorts,
  limit,
  skip,
  data,
  id,
  expand,
  select,
  useCache = false,
  cacheMaxSeconds = 3600,
  client,
  customerId,
  allowBeforeAuth,
  timeout = 15_000,
  bearerToken,
  apply,
  queryFields,
}: ApiRequestOptions): Promise<ApiSuccessResponse<T> | Blob> {
  const queryStrings = serializeQuery({
    filters,
    sorts,
    limit,
    skip,
    expand,
    select,
    apply,
    queryFields,
  });

  const url = `${apiBase}${module}${Api.V1}${resource}${id ? `/${id}` : ''}${insertActionParameters(action, actionParameters)}?${queryStrings.join('&')}`;
  const additionalHeaders: { [key: string]: string } = data !== undefined ? {'Content-Type': 'application/json'} : {};
  const settingsStore = useSettings(pinia);

  await settingsStore.waitReady();
  if (!allowBeforeAuth) {
    await settingsStore.waitReady();
  }
  if (settingsStore.userToken) {
    additionalHeaders.Authorization = `Bearer ${settingsStore.userToken.Token}`;
  }

  const token = bearerToken ?? (await settingsStore.getUserToken(false, allowBeforeAuth))?.Token;
  if (token) {
    additionalHeaders.Authorization = `Bearer ${token}`;
  }

  await settingsStore.waitReady();

  const currentCustomerId = customerId ?? settingsStore.customerId;

  if (currentCustomerId) {
    additionalHeaders['X-Customer-Id'] = currentCustomerId;
  }

  const currentClient = client ?? settingsStore.client;
  if (currentClient) {
    additionalHeaders['X-l3c-Mandant'] = String(currentClient);
  }

  const currentTime = (new Date()).getTime() / 1000;

  function doRequest() {
    return CapacitorHttp.request({
      method,
      url,
      data,
      headers: {
        'Accept': accept,
        ...additionalHeaders,
      },
      responseType: accept === Accept.OctetStream ? 'blob' : 'json',
      connectTimeout: timeout,
      readTimeout: timeout,
    });
  }

  let response: HttpResponse;

  function fillCache() {
    if (
      currentCustomerId == null ||
      currentClient == null
    ) return;


    if (!apiCache[currentCustomerId]) apiCache[currentCustomerId] = {};
    if (!apiCache[currentCustomerId]![currentClient]) apiCache[currentCustomerId]![currentClient] = {};
    apiCache[currentCustomerId]![currentClient]![url] = {
      updatedTimestamp: currentTime,
      data: doRequest(),
    };
  }

  if (
    useCache &&
    currentCustomerId != null &&
    currentClient != null &&
    method === Method.Get
  ) {
    if (
      !apiCache[currentCustomerId]?.[currentClient]?.[url] ||
      (apiCache[currentCustomerId]?.[currentClient]?.[url]?.updatedTimestamp ?? 0) < currentTime - cacheMaxSeconds
    ) {
      // not yet cached
      fillCache();
      response = await apiCache[currentCustomerId]![currentClient]![url]!.data;
    } else {
      // cached
      try {
        response = await apiCache[currentCustomerId]![currentClient]![url]!.data;
      } catch (err) {
        // retry if cached value is error
        fillCache();
        response = await apiCache[currentCustomerId]![currentClient]![url]!.data;
      }
    }
  } else {
    response = await doRequest();
  }

  function clearCacheItem() {
    if (!useCache || currentCustomerId == null || currentClient == null || method !== Method.Get) return;
    if (!apiCache[currentCustomerId]?.[currentClient]?.[url]) return;
    delete apiCache[currentCustomerId]![currentClient]![url];
  }

  if (accept === Accept.OctetStream) {
    if (response.status < 200 || response.status >= 300) {
      clearCacheItem();
      throw new ApiError(response.status, `HTTP error: ${response.status}`);
    }

    return base64ToBlob(response.data);
  }

  const parsed: ApiResponse<T> = response.data;

  if (isError(parsed)) {
    clearCacheItem();
    const parsedError = sanitizeError(parsed);
    throw new ApiError(parsedError.code ?? response.status, parsedError.error);
  }

  if (response.status < 200 || response.status >= 300) {
    clearCacheItem();
    throw new ApiError(response.status, `HTTP error: ${response.status}`);
  }

  return parsed;
}

export async function doApiRequest<T = any>(data: ApiJsonRequestOptions): Promise<ApiSuccessResponse<T>>;
export async function doApiRequest(data: ApiBlobRequestOptions): Promise<Blob>;
export async function doApiRequest<T = any>(data: ApiRequestOptions): Promise<ApiSuccessResponse<T> | Blob> {
  let currentError = null as Error | null;
  let timePassed = false;
  const timeout = setTimeout(() => timePassed = true, data.timeout ?? 15_000);
  while (true) {
    try {
      const response = await doActualApiRequest(data);
      clearTimeout(timeout);
      return response;
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
      currentError = err as Error;
      if (err.code === 400 || err.code === 404) {
        break;
      }
      if (timePassed) break;
    }
    if (data.one_shot) break;
  }

  clearTimeout(timeout);
  console.log(currentError);
  throw currentError;
}
